// Eşleştirilemeyen içerik çekmecesi — Geçmiş Rapor Aktarımı (Task 13, tasarım review 18A).
//
// 18A: T13 metnindeki `UnmappedPopover` İPTAL. Aynı bileşen iki yerden açılır:
//   · İşler/Tamamlanan satırındaki "eşlenmemiş içerik (n)" düğmesi (bu görev)
//   · ProjectDetail "Rapor aktarımı" alanındaki "göster" linki (Task 14)
// Her ikisi de yalnız `importId` verir; içerik `GET imports/:id` ile burada çekilir
// (liste/SSE DTO'su `unmappedContent` TAŞIMAZ — api-route-table.md).
//
// Sağ 480 px, tam yükseklik, role=dialog + aria-modal, Esc kapatır, odak tuzağı.
// Sol içindekiler + sağ tam metin (pre-wrap 14 px), parça başına Kopyala (1,5 sn
// "Kopyalandı"), izinle "Raporda aç (s.N)".

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReportImportDetail, UnmappedContentItem } from "@mahir/shared";
import { getImport } from "../../api/report-import";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  fileOpenUrl,
  importFileUrl,
  primaryImportFile,
} from "../../lib/report-import-ui";

/** 18A — uzun parçalarda önce ilk 600 karakter gösterilir. */
const SNIPPET_LIMIT = 600;

export interface UnmappedDrawerProps {
  /** Açık çekmecenin import kimliği; `null` iken hiçbir şey render edilmez. */
  importId: string | null;
  onClose: () => void;
  /** "Raporda aç" linki yalnız `projects.import_report` izniyle görünür. */
  canOpenReport: boolean;
}

/** `"PDF s.44–46"` → 44 (ilk sayfa); bulunamazsa null. */
function firstPageOf(sourceRef: string): number | null {
  const m = /(\d+)/.exec(sourceRef);
  return m ? Number(m[1]) : null;
}

export function UnmappedDrawer({
  importId,
  onClose,
  canOpenReport,
}: UnmappedDrawerProps): JSX.Element | null {
  const titleId = useId();
  const hintId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [detail, setDetail] = useState<ReportImportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const load = useCallback((id: string): void => {
    setLoading(true);
    setError(null);
    getImport(id)
      .then((res) => {
        setDetail(res.import);
        setActiveIdx(0);
      })
      .catch(() => setError(IMPORT_COPY.listRefreshFailed))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!importId) {
      setDetail(null);
      setExpanded({});
      setCopiedIdx(null);
      return;
    }
    load(importId);
  }, [importId, load]);

  // Esc + odak tuzağı (ConfirmDialog ile aynı desen) + kapanışta odağın
  // tetikleyiciye iadesi (fix round 1 — klavye kullanıcısı listede kaldığı yere döner).
  useEffect(() => {
    if (!importId) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Tetikleyici hâlâ DOM'daysa odağı ona iade et.
      if (trigger?.isConnected === true) trigger.focus();
    };
  }, [importId, onClose]);

  // Kopyalandı geri bildirimi 1,5 sn sonra söner.
  useEffect(() => {
    if (copiedIdx === null) return;
    const t = window.setTimeout(() => setCopiedIdx(null), 1500);
    return () => window.clearTimeout(t);
  }, [copiedIdx]);

  if (!importId) return null;

  const items: UnmappedContentItem[] = detail?.unmappedContent ?? [];
  const active = items[activeIdx] ?? null;
  const primary = detail ? primaryImportFile(detail.files) : null;

  function copyItem(item: UnmappedContentItem, idx: number): void {
    const text = `${item.title}\n${item.sourceRef}\n\n${item.text}`;
    void navigator.clipboard?.writeText(text).then(
      () => setCopiedIdx(idx),
      () => setCopiedIdx(null),
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 120,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={hintId}
        tabIndex={-1}
        style={{
          width: 480,
          maxWidth: "100%",
          height: "100%",
          background: "var(--color-surface)",
          borderLeft: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <header
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId} style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>
              {fillTemplate(IMPORT_COPY.unmappedTitle, { n: items.length })}
            </h2>
            <p
              id={hintId}
              style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "var(--color-text-2)" }}
            >
              {IMPORT_COPY.unmappedBandHint}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={IMPORT_COPY.closeGeneric}
            style={{
              marginLeft: "auto",
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
              color: "var(--color-text-2)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </header>

        {loading ? (
          // İskelet (8A — LOADING).
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{ height: 14, borderRadius: 6, background: "var(--color-bg)" }}
                aria-hidden="true"
              />
            ))}
            <span style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }} role="status">
              {IMPORT_COPY.loadingGeneric}
            </span>
          </div>
        ) : error ? (
          <div style={{ padding: 16 }}>
            <p style={{ fontSize: "0.85rem", color: "var(--color-text-2)", margin: "0 0 10px" }}>
              {error}
            </p>
            <button
              type="button"
              onClick={() => load(importId)}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                border: "1px solid var(--color-border)",
                background: "var(--color-surface)",
                color: "var(--color-text)",
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              {IMPORT_COPY.retryGeneric}
            </button>
          </div>
        ) : items.length === 0 ? (
          <p style={{ padding: 16, fontSize: "0.85rem", color: "var(--color-text-2)" }}>
            {IMPORT_COPY.unmappedEmpty}
          </p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", flex: 1, minHeight: 0 }}>
            {/* Sol — içindekiler */}
            <nav
              aria-label={IMPORT_COPY.unmappedToc}
              style={{
                borderRight: "1px solid var(--color-border)",
                overflowY: "auto",
                padding: 8,
              }}
            >
              {items.map((item, idx) => (
                <button
                  key={`${item.title}-${idx.toString()}`}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  aria-current={idx === activeIdx ? "true" : undefined}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 8px",
                    marginBottom: 2,
                    borderRadius: 6,
                    border: "none",
                    fontSize: "0.78rem",
                    cursor: "pointer",
                    background:
                      idx === activeIdx
                        ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                        : "transparent",
                    color: idx === activeIdx ? "var(--color-primary)" : "var(--color-text-2)",
                    fontWeight: idx === activeIdx ? 600 : 400,
                  }}
                >
                  {item.title}
                </button>
              ))}
            </nav>

            {/* Sağ — tam metin */}
            <div style={{ overflowY: "auto", padding: "12px 14px" }}>
              {active ? (
                <>
                  <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{active.title}</div>
                  <div
                    style={{
                      fontFamily: "var(--font-mono, monospace)",
                      fontSize: "0.75rem",
                      color: "var(--color-text-2)",
                      marginTop: 2,
                    }}
                  >
                    {active.sourceRef}
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      font: "inherit",
                      fontSize: "0.875rem",
                      color: "var(--color-text)",
                      margin: "10px 0",
                    }}
                  >
                    {expanded[activeIdx] === true
                      ? active.text
                      : active.text.slice(0, SNIPPET_LIMIT)}
                  </pre>
                  {active.text.length > SNIPPET_LIMIT && expanded[activeIdx] !== true ? (
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [activeIdx]: true }))}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "var(--color-primary)",
                        textDecoration: "underline",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      {IMPORT_COPY.expand}
                    </button>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginTop: 12,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => copyItem(active, activeIdx)}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 6,
                        border: "1px solid var(--color-border)",
                        background: "var(--color-surface)",
                        color: "var(--color-text)",
                        fontSize: "0.82rem",
                        cursor: "pointer",
                      }}
                    >
                      {copiedIdx === activeIdx ? IMPORT_COPY.copied : IMPORT_COPY.copy}
                    </button>
                    {canOpenReport && primary && firstPageOf(active.sourceRef) !== null ? (
                      <a
                        href={fileOpenUrl(importFileUrl(primary), primary.kind, [
                          firstPageOf(active.sourceRef)!,
                        ])}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "var(--color-primary)",
                          textDecoration: "underline",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                        }}
                      >
                        {fillTemplate(IMPORT_COPY.openInReport, {
                          page: firstPageOf(active.sourceRef)!,
                        })}
                      </a>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
